import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Card, EmptyState, TableSkeleton } from '@/components/ui';
import { formatRelative } from '@/utils/format';
import type { OverviewSnapshot } from '@/types/api';

/** What is wrong with a site's protection, and what to do about it, in a line each. */
const PROBLEMS: Record<string, { label: string; tone: string }> = {
  restore_failed: { label: 'проверка восстановления не прошла', tone: 'tone-down' },
  missing: { label: 'нет ни одной проверенной копии', tone: 'tone-down' },
  stale: { label: 'копия устарела', tone: 'tone-degraded' },
  not_connected: { label: 'сервер не подключён, копии не создаются', tone: 'tone-degraded' },
  restore_untested: { label: 'восстановление ещё не проверялось', tone: 'muted' },
};

/**
 * Whether each site could be brought back today: a recent copy, and the weekly
 * proof that copies restore. The numbers say how much of the network is
 * covered; the list says which sites are not, and why.
 */
export function ProtectionCard({ overview }: { readonly overview: OverviewSnapshot | null | undefined }): ReactNode {
  const protection = overview?.protection;

  return (
    <Card
      title="Копии и восстановление"
      icon={ShieldCheck}
      flush
      actions={
        <Link to="/backups" className="btn btn-ghost btn-sm">
          Все копии
        </Link>
      }
    >
      {overview === undefined ? (
        <TableSkeleton rows={3} columns={2} />
      ) : !protection || protection.sites === 0 ? (
        <EmptyState icon={ShieldCheck} message="Сайтов пока нет" />
      ) : (
        <div className="stack stack-tight" style={{ padding: 'var(--space-4)' }}>
          <div className="row row-wrap" style={{ gap: 'var(--space-6)' }}>
            <Figure value={protection.covered} of={protection.sites} label="со свежей копией" tone={protection.covered === protection.sites ? 'tone-healthy' : ''} />
            <Figure value={protection.restoreProven} of={protection.sites} label="восстановление проверено" tone={protection.restoreFailed > 0 ? 'tone-down' : ''} />
            {protection.missing + protection.stale > 0 ? <Figure value={protection.missing + protection.stale} label="без свежей копии" tone="tone-down" /> : null}
            {protection.notConnected > 0 ? <Figure value={protection.notConnected} label="сервер не подключён" tone="tone-degraded" /> : null}
          </div>
          {protection.problems.length > 0 ? (
            <div className="table-scroll">
              <table>
                <tbody>
                  {protection.problems.map((item) => {
                    const problem = PROBLEMS[item.problem] ?? { label: item.problem, tone: '' };
                    return (
                      <tr key={item.siteId}>
                        <td>
                          <Link to={`/sites/${item.siteId}`} className="strong">
                            {item.name}
                          </Link>
                        </td>
                        <td className={`text-sm ${problem.tone}`}>{problem.label}</td>
                        <td className="text-sm muted nowrap">{item.lastVerifiedAt ? `копия ${formatRelative(item.lastVerifiedAt)}` : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <span className="text-sm tone-healthy">У всех сайтов есть копии с проверенным восстановлением.</span>
          )}
        </div>
      )}
    </Card>
  );
}

function Figure({ value, of, label, tone }: { readonly value: number; readonly of?: number; readonly label: string; readonly tone: string }): ReactNode {
  return (
    <div className="stack" style={{ gap: 0 }}>
      <span className={`strong ${tone}`} style={{ fontSize: 'var(--text-2xl)' }}>
        {value}
        {of !== undefined ? <span className="muted text-sm"> из {of}</span> : null}
      </span>
      <span className="text-sm muted">{label}</span>
    </div>
  );
}
